/**
 * UsoIAPage — Reportes de uso y costo de IA por rango de fechas (solo admin).
 *
 * Fuente de datos:
 *  - `ai_usage_daily` (agregado por módulo+modelo+día, escrito por
 *    `registrarUsoIA`): usado para KPIs, tendencia y desgloses por módulo/
 *    modelo — pocos docs incluso en rangos largos, rápido de consultar.
 *  - `ai_usage` (crudo, un doc por llamada): usado SOLO para el costo por
 *    consulta médica — se limita a `RAW_LIMIT` documentos más recientes del
 *    rango (con el conteo real mostrado) para no escanear colecciones enormes
 *    en producción; ver nota de transparencia en la sección correspondiente.
 *
 * Precios: editable aquí mismo (`config/aiPricing`, mismo patrón de
 * `setDoc(..., {merge:true})` que usa ConfigIAPage para `config/aiModels`).
 * Cambiar un precio NO recalcula el costo ya registrado — cada llamada
 * guardó su costo con el precio vigente en ese momento.
 */
import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection, query, where, orderBy, limit, getDocs, getCountFromServer,
  doc, getDoc, setDoc, serverTimestamp,
} from 'firebase/firestore';
import {
  AreaChart, Area, BarChart, Bar, ComposedChart, Line, PieChart, Pie, Cell, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  Coins, Loader2, RefreshCw, TrendingUp, Activity, Save, Info, Layers, Cpu,
  Mic, Volume2, Brain, DatabaseZap, Clock, ListOrdered,
} from 'lucide-react';
import EstadoMonitoreoBanner from '../../components/costos/EstadoMonitoreoBanner';
import PreciosVigencia from '../../components/costos/PreciosVigencia';
import ServiciosUsoPanel from '../../components/costos/ServiciosUsoPanel';
import TooltipGrafico from '../../components/costos/TooltipGrafico';
import { aFecha, etiquetaHora, periodoHoraHace, ultimasHoras, type FirestoreTs } from '../../components/costos/costosUtil';
import MainLayout from '../../components/layout/MainLayout';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import {
  moduloLabel, fmtTokens, fmtCosto, fmtMinutos, fmtCaracteres, fmtCantidad,
  nombreModelo, tipoDe, type TipoUsoIA,
} from '../../utils/usoIaFormato';
import dashStyles from './AdminDashboardPage.module.css';
import styles from './UsoIAPage.module.css';

const PANAMA_TZ = 'America/Panama';
const RAW_LIMIT = 3000;

// Respaldo de precios — DEBE coincidir con PRECIOS_*_DEFAULT en
// functions/src/utils/registrarUsoIA.ts (texto) y
// livekit-agent-shared/usage_tracking.py (voz); se usa solo para pre-rellenar
// el editor si config/aiPricing aún no tiene el submapa correspondiente.
const PRECIOS_LLM_DEFAULT: Record<string, { in: number; out: number }> = {
  'gemini-3.5-flash': { in: 0.30, out: 2.50 },
  'gemini-3.1-flash-lite': { in: 0.10, out: 0.40 },
  'gemini-2.5-pro': { in: 1.25, out: 10.00 },
  'gemini-2.5-flash': { in: 0.30, out: 2.50 },
  'gemini-2.5-flash-lite': { in: 0.10, out: 0.40 },
  'gemini-1.5-flash': { in: 0.075, out: 0.30 },
  'gemini-1.5-flash-latest': { in: 0.075, out: 0.30 },
};
const PRECIOS_STT_DEFAULT: Record<string, number> = {  // USD por MINUTO de audio
  'livekit.plugins.deepgram.stt.STT': 0.0043,
  'livekit.plugins.google.stt.STT': 0.024,
  'livekit.plugins.assemblyai.stt.STT': 0.015,
  'livekit.plugins.speechmatics.stt.STT': 0.030,
  'livekit.plugins.cartesia.stt.STT': 0.020,
};
const PRECIOS_TTS_DEFAULT: Record<string, number> = {  // USD por MILLÓN de caracteres
  'livekit.plugins.google.tts.TTS': 16.0,
  'livekit.plugins.deepgram.tts.TTS': 15.0,
  'livekit.plugins.elevenlabs.tts.TTS': 300.0,
  'livekit.plugins.cartesia.tts.TTS': 60.0,
  'livekit.plugins.xai.tts.TTS': 100.0,
};

const CHART_COLORS = ['#6366f1', '#22c55e', '#f59e0b', '#3b82f6', '#ef4444', '#8b5cf6', '#14b8a6', '#f97316', '#ec4899'];

function panamaToday(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: PANAMA_TZ }).format(new Date());
}

function addDaysStr(fecha: string, days: number): string {
  const d = new Date(`${fecha}T00:00:00-05:00`); // Panamá: UTC-5 fijo, sin DST
  d.setDate(d.getDate() + days);
  return new Intl.DateTimeFormat('en-CA', { timeZone: PANAMA_TZ }).format(d);
}

function diffDays(start: string, end: string): number {
  const a = new Date(`${start}T00:00:00-05:00`).getTime();
  const b = new Date(`${end}T00:00:00-05:00`).getTime();
  return Math.max(0, Math.round((b - a) / 86_400_000));
}

type PresetKey = 'hoy' | '7d' | '30d' | '60d' | 'custom';
type Granularidad = 'dia' | 'semana' | 'mes';

const PRESETS: Array<{ key: PresetKey; label: string; days: number }> = [
  { key: 'hoy', label: 'Hoy', days: 0 },
  { key: '7d', label: 'Últimos 7 días', days: 6 },
  { key: '30d', label: 'Últimos 30 días', days: 29 },
  { key: '60d', label: 'Últimos 60 días', days: 59 },
];

const MESES_CORTOS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

function bucketKey(fecha: string, g: Granularidad): string {
  if (g === 'mes') return fecha.slice(0, 7);
  if (g === 'dia') return fecha;
  const d = new Date(`${fecha}T00:00:00-05:00`);
  const dow = d.getDay(); // 0=domingo..6=sábado
  const diffToMonday = dow === 0 ? -6 : 1 - dow;
  d.setDate(d.getDate() + diffToMonday);
  return new Intl.DateTimeFormat('en-CA', { timeZone: PANAMA_TZ }).format(d);
}

function formatPeriodo(periodo: string, g: Granularidad): string {
  if (g === 'mes') {
    const [y, m] = periodo.split('-');
    return `${MESES_CORTOS[parseInt(m, 10) - 1]} ${y}`;
  }
  const [, m, d] = periodo.split('-');
  return g === 'semana' ? `sem ${d}/${m}` : `${d}/${m}`;
}

interface UsoIaDiarioDoc {
  modulo: string;
  modelo: string;
  tipo?: string;
  fecha: string;
  tokensIn: number;
  tokensOut: number;
  tokensTotal: number;
  /** Subconjunto de tokensIn que se cobra con descuento (docs viejos: sin el campo). */
  tokensCache?: number;
  /** Razonamiento del modelo; se cobra como salida (docs viejos: sin el campo). */
  tokensThoughts?: number;
  audioSegundos?: number;
  caracteres?: number;
  costoEstimado: number;
  llamadas: number;
}

/** Una llamada individual de `ai_usage` (para «Últimas llamadas»). */
interface UsoIaLlamadaDoc {
  modulo: string;
  modelo: string;
  tipo?: string;
  fecha?: string;
  hora?: string;
  creadoEn?: FirestoreTs;
  tokensIn?: number;
  tokensOut?: number;
  tokensCache?: number;
  tokensThoughts?: number;
  tokensTotal?: number;
  audioSegundos?: number;
  caracteres?: number;
  costoEntrada?: number;
  costoCache?: number;
  costoSalida?: number;
  costoEstimado?: number;
  consultaId?: string | null;
}

/** Agregado por hora de `ai_usage_hourly`. */
interface UsoIaHoraDoc {
  fecha: string;
  hora: string;
  costoEstimado?: number;
  llamadas?: number;
}

interface UsoIaCallDoc {
  modulo: string;
  modelo: string;
  tipo?: string;
  tokensTotal: number;
  audioSegundos?: number;
  caracteres?: number;
  costoEstimado: number;
  consultaId: string | null;
  fecha: string;
}

interface Acumulado {
  tokensIn: number;
  tokensOut: number;
  tokensTotal: number;
  tokensCache: number;
  tokensThoughts: number;
  audioSegundos: number;
  caracteres: number;
  costoEstimado: number;
  llamadas: number;
}

interface AcumuladoModelo extends Acumulado {
  tipo: TipoUsoIA;
}

function ChartTooltip({ active, payload, label }: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className={dashStyles.chartTooltip}>
      {label && <p className={dashStyles.chartTooltipLabel}>{label}</p>}
      {payload.map((entry) => (
        <p key={entry.name} className={dashStyles.chartTooltipRow}>
          <span className={dashStyles.legendDot} style={{ background: entry.color }} />
          {entry.name}: <strong>{entry.name.toLowerCase().includes('costo') ? fmtCosto(entry.value) : fmtTokens(entry.value)}</strong>
        </p>
      ))}
    </div>
  );
}

export default function UsoIAPage() {
  const { user } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();

  // ── Rango de fechas ──────────────────────────────────────────
  const [preset, setPreset] = useState<PresetKey>('30d');
  const [customStart, setCustomStart] = useState(() => addDaysStr(panamaToday(), -29));
  const [customEnd, setCustomEnd] = useState(() => panamaToday());

  const { startFecha, endFecha } = useMemo(() => {
    if (preset === 'custom') {
      return customStart <= customEnd
        ? { startFecha: customStart, endFecha: customEnd }
        : { startFecha: customEnd, endFecha: customStart };
    }
    const p = PRESETS.find((x) => x.key === preset)!;
    const end = panamaToday();
    return { startFecha: addDaysStr(end, -p.days), endFecha: end };
  }, [preset, customStart, customEnd]);

  const [granularidad, setGranularidad] = useState<Granularidad>('dia');
  useEffect(() => {
    const dias = diffDays(startFecha, endFecha);
    if (dias <= 14) setGranularidad('dia');
    else if (dias <= 90) setGranularidad('semana');
    else setGranularidad('mes');
  }, [startFecha, endFecha]);

  const [metrica, setMetrica] = useState<'costo' | 'tokens'>('costo');

  // ── Datos agregados (ai_usage_daily) ─────────────────────────
  const [dailyDocs, setDailyDocs] = useState<UsoIaDiarioDoc[]>([]);
  const [loadingDaily, setLoadingDaily] = useState(true);

  // ── Datos crudos para costo por consulta (ai_usage, limitado) ─
  const [rawDocs, setRawDocs] = useState<UsoIaCallDoc[]>([]);
  const [rawTotalCount, setRawTotalCount] = useState<number | null>(null);
  const [loadingRaw, setLoadingRaw] = useState(true);

  const [refreshing, setRefreshing] = useState(false);

  // ── Pestañas ─────────────────────────────────────────────────
  const [tab, setTab] = useState<'ia' | 'servicios'>('ia');

  // ── Últimas llamadas (50 más recientes, sin filtro de fecha) ──
  const [recientes, setRecientes] = useState<UsoIaLlamadaDoc[]>([]);
  const [loadingRecientes, setLoadingRecientes] = useState(true);
  const [errorRecientes, setErrorRecientes] = useState(false);

  // ── Costo y llamadas por hora (últimas 48 h, ai_usage_hourly) ─
  const [horaDocs, setHoraDocs] = useState<UsoIaHoraDoc[]>([]);
  const [loadingHora, setLoadingHora] = useState(true);
  const [errorHora, setErrorHora] = useState(false);

  const cargarRecientes = useCallback(async () => {
    try {
      const snap = await getDocs(query(collection(db, 'ai_usage'), orderBy('creadoEn', 'desc'), limit(50)));
      setRecientes(snap.docs.map((d) => d.data() as UsoIaLlamadaDoc));
      setErrorRecientes(false);
    } catch (err) {
      console.error('[UsoIAPage] error cargando últimas llamadas', err);
      setRecientes([]);
      setErrorRecientes(true);
    }
  }, []);

  const cargarHoras = useCallback(async () => {
    try {
      // Un solo rango sobre `fecha` (sin índice compuesto); el corte de 48 h se aplica en memoria.
      const desde = addDaysStr(panamaToday(), -2);
      const snap = await getDocs(query(collection(db, 'ai_usage_hourly'), where('fecha', '>=', desde)));
      setHoraDocs(snap.docs.map((d) => d.data() as UsoIaHoraDoc));
      setErrorHora(false);
    } catch (err) {
      console.error('[UsoIAPage] error cargando ai_usage_hourly', err);
      setHoraDocs([]);
      setErrorHora(true);
    }
  }, []);

  const cargarDatos = useCallback(async () => {
    try {
      const q = query(
        collection(db, 'ai_usage_daily'),
        where('fecha', '>=', startFecha),
        where('fecha', '<=', endFecha),
        orderBy('fecha', 'asc'),
      );
      const snap = await getDocs(q);
      setDailyDocs(snap.docs.map((d) => d.data() as UsoIaDiarioDoc));
    } catch (err) {
      console.error('[UsoIAPage] error cargando ai_usage_daily', err);
      setDailyDocs([]);
    }
  }, [startFecha, endFecha]);

  const cargarConsultas = useCallback(async () => {
    try {
      const baseQ = query(
        collection(db, 'ai_usage'),
        where('fecha', '>=', startFecha),
        where('fecha', '<=', endFecha),
      );
      const [countSnap, docsSnap] = await Promise.all([
        getCountFromServer(baseQ),
        getDocs(query(baseQ, orderBy('fecha', 'desc'), limit(RAW_LIMIT))),
      ]);
      setRawTotalCount(countSnap.data().count);
      setRawDocs(docsSnap.docs.map((d) => d.data() as UsoIaCallDoc));
    } catch (err) {
      console.error('[UsoIAPage] error cargando ai_usage', err);
      setRawDocs([]);
      setRawTotalCount(0);
    }
  }, [startFecha, endFecha]);

  const cargarTodo = useCallback(async () => {
    setRefreshing(true);
    setLoadingDaily(true);
    setLoadingRaw(true);
    setLoadingRecientes(true);
    setLoadingHora(true);
    await Promise.all([cargarDatos(), cargarConsultas(), cargarRecientes(), cargarHoras()]);
    setLoadingDaily(false);
    setLoadingRaw(false);
    setLoadingRecientes(false);
    setLoadingHora(false);
    setRefreshing(false);
  }, [cargarDatos, cargarConsultas, cargarRecientes, cargarHoras]);

  useEffect(() => { cargarTodo(); }, [cargarTodo]);

  // ── Agregaciones (por módulo, por modelo, por fecha) ─────────
  // NOTA de unidades: un módulo (ej. "triage_voz") puede mezclar filas de
  // LLM (tokens) + STT (minutos) + TTS (caracteres) — por eso "detalle por
  // módulo" muestra las 3 columnas por separado. Un MODELO, en cambio,
  // siempre corresponde a un solo tipo (un modelo de Gemini nunca es STT),
  // así que "detalle por modelo" puede mostrar una sola columna "Cantidad".
  const resumen = useMemo(() => {
    let tokensTotal = 0, tokensThoughts = 0, tokensCache = 0, audioSegundos = 0, caracteres = 0, costoEstimado = 0, llamadas = 0;
    const porModulo = new Map<string, Acumulado>();
    const porModelo = new Map<string, AcumuladoModelo>();

    const vacio = (): Acumulado => ({ tokensIn: 0, tokensOut: 0, tokensTotal: 0, tokensCache: 0, tokensThoughts: 0, audioSegundos: 0, caracteres: 0, costoEstimado: 0, llamadas: 0 });
    const sumar = (acc: Acumulado, d: UsoIaDiarioDoc) => {
      acc.tokensIn += d.tokensIn; acc.tokensOut += d.tokensOut;
      acc.tokensTotal += d.tokensTotal; acc.tokensCache += d.tokensCache ?? 0; acc.tokensThoughts += d.tokensThoughts ?? 0;
      acc.audioSegundos += d.audioSegundos ?? 0; acc.caracteres += d.caracteres ?? 0;
      acc.costoEstimado += d.costoEstimado; acc.llamadas += d.llamadas;
    };

    for (const d of dailyDocs) {
      tokensTotal += d.tokensTotal; tokensThoughts += d.tokensThoughts ?? 0; tokensCache += d.tokensCache ?? 0;
      audioSegundos += d.audioSegundos ?? 0; caracteres += d.caracteres ?? 0;
      costoEstimado += d.costoEstimado; llamadas += d.llamadas;
      // `modelo` no existía en ai_usage_daily antes del cambio de esquema a
      // {modulo}_{modelo}_{fecha} — docs viejos caen en 'desconocido' en vez
      // de romper la agregación con una clave `undefined`.
      const modulo = d.modulo || 'desconocido';
      const modelo = d.modelo || 'desconocido';
      sumar(porModulo.get(modulo) ?? (porModulo.set(modulo, vacio()), porModulo.get(modulo)!), d);
      const filaModelo = porModelo.get(modelo) ?? (() => {
        const nueva = { ...vacio(), tipo: tipoDe(d.tipo) };
        porModelo.set(modelo, nueva);
        return nueva;
      })();
      sumar(filaModelo, d);
    }

    const filasModulo = [...porModulo.entries()]
      .map(([modulo, v]) => ({ modulo, ...v }))
      .sort((a, b) => b.costoEstimado - a.costoEstimado);
    const filasModelo = [...porModelo.entries()]
      .map(([modelo, v]) => ({ modelo, ...v }))
      .sort((a, b) => b.costoEstimado - a.costoEstimado);

    return { tokensTotal, tokensThoughts, tokensCache, audioSegundos, caracteres, costoEstimado, llamadas, filasModulo, filasModelo };
  }, [dailyDocs]);

  const trendData = useMemo(() => {
    const buckets = new Map<string, { costoEstimado: number; tokensTotal: number; llamadas: number }>();
    for (const d of dailyDocs) {
      const key = bucketKey(d.fecha, granularidad);
      const b = buckets.get(key) ?? { costoEstimado: 0, tokensTotal: 0, llamadas: 0 };
      b.costoEstimado += d.costoEstimado; b.tokensTotal += d.tokensTotal; b.llamadas += d.llamadas;
      buckets.set(key, b);
    }
    return [...buckets.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, v]) => ({
        periodo: formatPeriodo(key, granularidad),
        Costo: Number(v.costoEstimado.toFixed(4)),
        Tokens: v.tokensTotal,
      }));
  }, [dailyDocs, granularidad]);

  const porConsulta = useMemo(() => {
    const map = new Map<string, { consultaId: string; fecha: string; tokensTotal: number; audioSegundos: number; caracteres: number; costoEstimado: number; llamadas: number; modulos: Set<string> }>();
    for (const d of rawDocs) {
      if (!d.consultaId) continue;
      const existente = map.get(d.consultaId);
      const c = existente ?? { consultaId: d.consultaId, fecha: d.fecha, tokensTotal: 0, audioSegundos: 0, caracteres: 0, costoEstimado: 0, llamadas: 0, modulos: new Set<string>() };
      c.tokensTotal += d.tokensTotal; c.audioSegundos += d.audioSegundos ?? 0; c.caracteres += d.caracteres ?? 0;
      c.costoEstimado += d.costoEstimado; c.llamadas += 1; c.modulos.add(d.modulo);
      if (d.fecha < c.fecha) c.fecha = d.fecha;
      map.set(d.consultaId, c);
    }
    const todas = [...map.values()].sort((a, b) => b.costoEstimado - a.costoEstimado);
    return { top: todas.slice(0, 50), totalConsultas: todas.length };
  }, [rawDocs]);

  // Últimas 48 h completas (rellena las horas sin llamadas para que la gráfica sea continua).
  const porHora = useMemo(() => {
    const corte = periodoHoraHace(48);
    const mapa = new Map<string, { costo: number; llamadas: number }>();
    for (const d of horaDocs) {
      const clave = `${d.fecha}T${d.hora}`;
      if (clave < corte) continue;
      const a = mapa.get(clave) ?? { costo: 0, llamadas: 0 };
      a.costo += d.costoEstimado ?? 0;
      a.llamadas += d.llamadas ?? 0;
      mapa.set(clave, a);
    }
    const datos = ultimasHoras(48).map((p) => ({
      periodo: p,
      hora: `${p.slice(11, 13)}h`,
      Costo: Number((mapa.get(p)?.costo ?? 0).toFixed(4)),
      Llamadas: mapa.get(p)?.llamadas ?? 0,
    }));
    return { datos, hayDatos: mapa.size > 0 };
  }, [horaDocs]);

  // ── Editor de precios (config/aiPricing) ─────────────────────
  // 3 submapas independientes: LLM ($/1M tokens), STT ($/minuto de audio),
  // TTS ($/1M caracteres) — mismo doc de Firestore, campos separados.
  const [preciosLLM, setPreciosLLM] = useState<Record<string, { in: number; out: number }>>({});
  const [preciosSTT, setPreciosSTT] = useState<Record<string, number>>({});
  const [preciosTTS, setPreciosTTS] = useState<Record<string, number>>({});
  const [preciosLoading, setPreciosLoading] = useState(true);
  const [preciosSaving, setPreciosSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'config', 'aiPricing'));
        const data = snap.exists() ? snap.data() : undefined;
        setPreciosLLM({ ...PRECIOS_LLM_DEFAULT, ...(data?.modelos as Record<string, { in: number; out: number }> ?? {}) });
        setPreciosSTT({ ...PRECIOS_STT_DEFAULT, ...(data?.sttPorMinuto as Record<string, number> ?? {}) });
        setPreciosTTS({ ...PRECIOS_TTS_DEFAULT, ...(data?.ttsPorMillonCaracteres as Record<string, number> ?? {}) });
      } catch {
        setPreciosLLM(PRECIOS_LLM_DEFAULT);
        setPreciosSTT(PRECIOS_STT_DEFAULT);
        setPreciosTTS(PRECIOS_TTS_DEFAULT);
      } finally {
        setPreciosLoading(false);
      }
    })();
  }, []);

  // Modelos vistos en los datos pero sin precio configurado → agregarlos con
  // 0 para que el admin los complete, en vez de quedar invisibles.
  useEffect(() => {
    if (resumen.filasModelo.length === 0) return;
    setPreciosLLM((prev) => {
      let cambio = false;
      const next = { ...prev };
      for (const f of resumen.filasModelo) {
        if (f.tipo === 'llm' && f.modelo !== 'desconocido' && !(f.modelo in next)) { next[f.modelo] = { in: 0, out: 0 }; cambio = true; }
      }
      return cambio ? next : prev;
    });
    setPreciosSTT((prev) => {
      let cambio = false;
      const next = { ...prev };
      for (const f of resumen.filasModelo) {
        if (f.tipo === 'stt' && !(f.modelo in next)) { next[f.modelo] = 0; cambio = true; }
      }
      return cambio ? next : prev;
    });
    setPreciosTTS((prev) => {
      let cambio = false;
      const next = { ...prev };
      for (const f of resumen.filasModelo) {
        if (f.tipo === 'tts' && !(f.modelo in next)) { next[f.modelo] = 0; cambio = true; }
      }
      return cambio ? next : prev;
    });
  }, [resumen.filasModelo]);

  function actualizarPrecioLLM(modelo: string, campo: 'in' | 'out', valor: string) {
    const num = parseFloat(valor);
    setPreciosLLM((prev) => ({ ...prev, [modelo]: { ...prev[modelo], [campo]: Number.isFinite(num) ? num : 0 } }));
  }
  function actualizarPrecioSTT(modelo: string, valor: string) {
    const num = parseFloat(valor);
    setPreciosSTT((prev) => ({ ...prev, [modelo]: Number.isFinite(num) ? num : 0 }));
  }
  function actualizarPrecioTTS(modelo: string, valor: string) {
    const num = parseFloat(valor);
    setPreciosTTS((prev) => ({ ...prev, [modelo]: Number.isFinite(num) ? num : 0 }));
  }

  async function guardarPrecios() {
    setPreciosSaving(true);
    try {
      await setDoc(doc(db, 'config', 'aiPricing'), {
        modelos: preciosLLM,
        sttPorMinuto: preciosSTT,
        ttsPorMillonCaracteres: preciosTTS,
        actualizadoEn: serverTimestamp(),
        actualizadoPor: user?.uid ?? null,
      }, { merge: true });
      addToast('success', 'Precios guardados. Se aplican a llamadas nuevas — no recalcula el costo ya registrado.');
    } catch {
      addToast('error', 'Error al guardar los precios.');
    } finally {
      setPreciosSaving(false);
    }
  }

  const isLoading = loadingDaily && dailyDocs.length === 0;
  // 'desconocido' es un valor sintético para docs sin `modelo` (datos previos
  // al cambio de esquema) — no es un modelo real que se pueda tarifar.
  const modelosLLMEditor = Object.keys(preciosLLM).filter((m) => m !== 'desconocido').sort();
  const modelosSTTEditor = Object.keys(preciosSTT).sort();
  const modelosTTSEditor = Object.keys(preciosTTS).sort();

  return (
    <MainLayout pageTitle="Uso de IA">
      <div className={dashStyles.page}>
        {/* ─── Header ──────────────────────────────────── */}
        <div className={dashStyles.header}>
          <div className={dashStyles.headerLeft}>
            <Coins size={22} />
            <div>
              <h1 className={dashStyles.title}>Uso de IA</h1>
              <p className={dashStyles.subtitle}>
                {tab === 'ia'
                  ? 'Tokens y costo por módulo, modelo y consulta médica'
                  : 'Consumo y costo de videollamadas, voz, infraestructura, biometría y costos fijos'}
              </p>
            </div>
          </div>
          {tab === 'ia' && (
            <button
              className={dashStyles.refreshBtn}
              onClick={cargarTodo}
              disabled={refreshing}
              type="button"
            >
              <RefreshCw size={14} className={refreshing ? dashStyles.spinning : ''} />
              Actualizar
            </button>
          )}
        </div>

        {/* ─── Qué servicios no se están midiendo (siempre visible) ─── */}
        <EstadoMonitoreoBanner />
        <PreciosVigencia />

        {/* ─── Pestañas ───────────────────────────────── */}
        <div className={styles.tabs} role="tablist" aria-label="Secciones de consumo y costo">
          {([['ia', 'Inteligencia artificial'], ['servicios', 'Todos los servicios']] as const).map(([clave, texto]) => (
            <button
              key={clave}
              type="button"
              role="tab"
              id={`tab-uso-${clave}`}
              aria-selected={tab === clave}
              aria-controls="panel-uso"
              tabIndex={tab === clave ? 0 : -1}
              className={`${styles.tab} ${tab === clave ? styles.tabActive : ''}`}
              onClick={() => setTab(clave)}
              onKeyDown={(e) => {
                if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
                  e.preventDefault();
                  const otra = clave === 'ia' ? 'servicios' : 'ia';
                  setTab(otra);
                  document.getElementById(`tab-uso-${otra}`)?.focus();
                }
              }}
            >
              {texto}
            </button>
          ))}
        </div>

        <div id="panel-uso" role="tabpanel" aria-labelledby={`tab-uso-${tab}`} className={styles.tabPanel}>

        {tab === 'servicios' ? (
          <ServiciosUsoPanel />
        ) : (
          <>
            {/* ─── Selector de rango ───────────────────────── */}
            <div className={styles.rangeBar}>
              {PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  className={`${styles.presetBtn} ${preset === p.key ? styles.presetBtnActive : ''}`}
                  onClick={() => setPreset(p.key)}
                >
                  {p.label}
                </button>
              ))}
              <button
                type="button"
                className={`${styles.presetBtn} ${preset === 'custom' ? styles.presetBtnActive : ''}`}
                onClick={() => setPreset('custom')}
              >
                Personalizado
              </button>
              {preset === 'custom' && (
                <div className={styles.customRange}>
                  <input
                    type="date"
                    value={customStart}
                    max={customEnd}
                    onChange={(e) => setCustomStart(e.target.value)}
                  />
                  <span style={{ color: 'var(--color-text-tertiary)' }}>a</span>
                  <input
                    type="date"
                    value={customEnd}
                    min={customStart}
                    max={panamaToday()}
                    onChange={(e) => setCustomEnd(e.target.value)}
                  />
                </div>
              )}
            </div>

            {isLoading ? (
              <div className={dashStyles.loadingWrap}>
                <Loader2 size={32} />
                <span>Cargando reporte de uso de IA...</span>
              </div>
            ) : (
              <>
                {/* ─── KPIs ────────────────────────────────── */}
                <div className={dashStyles.kpiGrid}>
                  <div className={dashStyles.kpiCard}>
                    <div className={dashStyles.kpiCardAccent} style={{ background: '#6366f1' }} />
                    <div className={dashStyles.kpiHeader}>
                      <p className={dashStyles.kpiLabel}>Tokens en el rango</p>
                      <div className={dashStyles.kpiIcon} style={{ background: 'rgba(99,102,241,0.1)', color: '#6366f1' }}>
                        <Activity size={16} />
                      </div>
                    </div>
                    <p className={dashStyles.kpiValue}>{fmtTokens(resumen.tokensTotal)}</p>
                    <p className={dashStyles.kpiMeta}>{startFecha} a {endFecha}</p>
                  </div>
                  <div className={dashStyles.kpiCard}>
                    <div className={dashStyles.kpiCardAccent} style={{ background: '#22c55e' }} />
                    <div className={dashStyles.kpiHeader}>
                      <p className={dashStyles.kpiLabel}>Costo estimado</p>
                      <div className={dashStyles.kpiIcon} style={{ background: 'rgba(34,197,94,0.1)', color: '#22c55e' }}>
                        <Coins size={16} />
                      </div>
                    </div>
                    <p className={dashStyles.kpiValue}>{fmtCosto(resumen.costoEstimado)}</p>
                    <p className={dashStyles.kpiMeta}>precios de referencia (editables abajo)</p>
                  </div>
                  <div className={dashStyles.kpiCard}>
                    <div className={dashStyles.kpiCardAccent} style={{ background: '#f59e0b' }} />
                    <div className={dashStyles.kpiHeader}>
                      <p className={dashStyles.kpiLabel}>Llamadas a IA</p>
                      <div className={dashStyles.kpiIcon} style={{ background: 'var(--color-warning-bg)', color: 'var(--color-warning)' }}>
                        <TrendingUp size={16} />
                      </div>
                    </div>
                    <p className={dashStyles.kpiValue}>{resumen.llamadas}</p>
                    <p className={dashStyles.kpiMeta}>
                      {resumen.llamadas > 0 ? `${fmtCosto(resumen.costoEstimado / resumen.llamadas)} / llamada` : '—'}
                    </p>
                  </div>
                  <div className={dashStyles.kpiCard}>
                    <div className={dashStyles.kpiCardAccent} style={{ background: '#8b5cf6' }} />
                    <div className={dashStyles.kpiHeader}>
                      <p className={dashStyles.kpiLabel}>Módulo con más costo</p>
                      <div className={dashStyles.kpiIcon} style={{ background: 'rgba(139,92,246,0.1)', color: '#8b5cf6' }}>
                        <Layers size={16} />
                      </div>
                    </div>
                    <p className={dashStyles.kpiValue} style={{ fontSize: 'var(--font-size-lg)' }}>
                      {resumen.filasModulo[0] ? moduloLabel(resumen.filasModulo[0].modulo) : '—'}
                    </p>
                    <p className={dashStyles.kpiMeta}>
                      {resumen.filasModulo[0] ? fmtCosto(resumen.filasModulo[0].costoEstimado) : '—'}
                    </p>
                  </div>
                  <div className={dashStyles.kpiCard}>
                    <div className={dashStyles.kpiCardAccent} style={{ background: '#14b8a6' }} />
                    <div className={dashStyles.kpiHeader}>
                      <p className={dashStyles.kpiLabel}>Minutos de audio (STT)</p>
                      <div className={dashStyles.kpiIcon} style={{ background: 'rgba(20,184,166,0.1)', color: '#14b8a6' }}>
                        <Mic size={16} />
                      </div>
                    </div>
                    <p className={dashStyles.kpiValue}>{fmtMinutos(resumen.audioSegundos)}</p>
                    <p className={dashStyles.kpiMeta}>transcripción + agentes de voz</p>
                  </div>
                  <div className={dashStyles.kpiCard}>
                    <div className={dashStyles.kpiCardAccent} style={{ background: '#f97316' }} />
                    <div className={dashStyles.kpiHeader}>
                      <p className={dashStyles.kpiLabel}>Caracteres sintetizados (TTS)</p>
                      <div className={dashStyles.kpiIcon} style={{ background: 'rgba(249,115,22,0.1)', color: '#f97316' }}>
                        <Volume2 size={16} />
                      </div>
                    </div>
                    <p className={dashStyles.kpiValue}>{fmtCaracteres(resumen.caracteres)}</p>
                    <p className={dashStyles.kpiMeta}>voz de los agentes al hablar</p>
                  </div>
                  <div className={dashStyles.kpiCard}>
                    <div className={dashStyles.kpiCardAccent} style={{ background: '#3b82f6' }} />
                    <div className={dashStyles.kpiHeader}>
                      <p className={dashStyles.kpiLabel}>Tokens de razonamiento</p>
                      <div className={dashStyles.kpiIcon} style={{ background: 'rgba(59,130,246,0.1)', color: '#3b82f6' }}>
                        <Brain size={16} />
                      </div>
                    </div>
                    <p className={dashStyles.kpiValue}>{fmtTokens(resumen.tokensThoughts)}</p>
                    <p className={dashStyles.kpiMeta}>«pensamiento» del modelo; se cobra como salida</p>
                  </div>
                  <div className={dashStyles.kpiCard}>
                    <div className={dashStyles.kpiCardAccent} style={{ background: '#ec4899' }} />
                    <div className={dashStyles.kpiHeader}>
                      <p className={dashStyles.kpiLabel}>Tokens en caché</p>
                      <div className={dashStyles.kpiIcon} style={{ background: 'rgba(236,72,153,0.1)', color: '#ec4899' }}>
                        <DatabaseZap size={16} />
                      </div>
                    </div>
                    <p className={dashStyles.kpiValue}>{fmtTokens(resumen.tokensCache)}</p>
                    <p className={dashStyles.kpiMeta}>parte de la entrada, cobrada con descuento</p>
                  </div>
                </div>

                {/* ─── Tendencia ───────────────────────────── */}
                <div className={dashStyles.section}>
                  <div className={dashStyles.sectionHeader}>
                    <h2 className={dashStyles.sectionTitle}>
                      <TrendingUp size={16} /> Tendencia
                    </h2>
                    <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                      <div className={styles.granularityBar}>
                        {(['dia', 'semana', 'mes'] as Granularidad[]).map((g) => (
                          <button
                            key={g}
                            type="button"
                            className={`${styles.granularityBtn} ${granularidad === g ? styles.granularityBtnActive : ''}`}
                            onClick={() => setGranularidad(g)}
                          >
                            {g === 'dia' ? 'Día' : g === 'semana' ? 'Semana' : 'Mes'}
                          </button>
                        ))}
                      </div>
                      <div className={styles.granularityBar}>
                        <button
                          type="button"
                          className={`${styles.granularityBtn} ${metrica === 'costo' ? styles.granularityBtnActive : ''}`}
                          onClick={() => setMetrica('costo')}
                        >
                          Costo
                        </button>
                        <button
                          type="button"
                          className={`${styles.granularityBtn} ${metrica === 'tokens' ? styles.granularityBtnActive : ''}`}
                          onClick={() => setMetrica('tokens')}
                        >
                          Tokens
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className={dashStyles.sectionBody}>
                    {trendData.length === 0 ? (
                      <p className={dashStyles.emptyState}>Sin datos de uso de IA en este rango.</p>
                    ) : (
                      <div className={dashStyles.chartWrap}>
                        <ResponsiveContainer width="100%" height={300}>
                          <AreaChart data={trendData} margin={{ left: 0, right: 20, top: 10, bottom: 5 }}>
                            <defs>
                              <linearGradient id="usoIaGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#6366f1" stopOpacity={0.35} />
                                <stop offset="95%" stopColor="#6366f1" stopOpacity={0.02} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-border-soft)" />
                            <XAxis dataKey="periodo" tick={{ fontSize: 12 }} />
                            <YAxis
                              tick={{ fontSize: 12 }}
                              tickFormatter={(v: number) => (metrica === 'costo' ? fmtCosto(v) : fmtTokens(v))}
                              allowDecimals={metrica === 'costo'}
                            />
                            <Tooltip content={<ChartTooltip />} />
                            <Area
                              type="monotone"
                              dataKey={metrica === 'costo' ? 'Costo' : 'Tokens'}
                              stroke="#6366f1"
                              strokeWidth={2}
                              fill="url(#usoIaGradient)"
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>
                </div>

                {/* ─── Por hora, últimas 48 h ──────────────── */}
                <div className={dashStyles.section}>
                  <div className={dashStyles.sectionHeader}>
                    <h2 className={dashStyles.sectionTitle}><Clock size={16} /> Por hora, últimas 48 h</h2>
                    <span className={dashStyles.sectionBadge}>hora de Panamá</span>
                  </div>
                  <div className={dashStyles.sectionBody}>
                    {loadingHora ? (
                      <p className={dashStyles.emptyState}>Cargando...</p>
                    ) : errorHora ? (
                      <p className={dashStyles.emptyState} role="alert">No se pudo leer el uso por hora.</p>
                    ) : !porHora.hayDatos ? (
                      <p className={dashStyles.emptyState}>Sin llamadas de IA en las últimas 48 horas.</p>
                    ) : (
                      <div
                        className={dashStyles.chartWrap}
                        role="img"
                        aria-label="Costo y llamadas de IA por hora durante las últimas 48 horas"
                      >
                        <ResponsiveContainer width="100%" height={280}>
                          <ComposedChart data={porHora.datos} margin={{ left: 0, right: 10, top: 10, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-border-soft)" />
                            <XAxis dataKey="hora" tick={{ fontSize: 10 }} interval={3} />
                            <YAxis yAxisId="costo" tick={{ fontSize: 11 }} tickFormatter={(v: number) => fmtCosto(v)} width={62} />
                            <YAxis yAxisId="llamadas" orientation="right" tick={{ fontSize: 11 }} allowDecimals={false} width={40} />
                            <Tooltip
                              content={(
                                <TooltipGrafico
                                  formato={(n, v) => (n === 'Costo' ? fmtCosto(v) : `${v}`)}
                                  titulo={(h) => {
                                    const f = porHora.datos.find((x) => x.hora === h);
                                    return f ? etiquetaHora(f.periodo) : h;
                                  }}
                                />
                              )}
                              cursor={{ fill: 'var(--color-surface-muted)' }}
                            />
                            <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                            <Bar yAxisId="costo" dataKey="Costo" fill="#6366f1" radius={[3, 3, 0, 0]} />
                            <Line yAxisId="llamadas" type="monotone" dataKey="Llamadas" stroke="#f59e0b" strokeWidth={2} dot={false} />
                          </ComposedChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>
                </div>

                {/* ─── Por módulo y por modelo ─────────────── */}
                <div className={dashStyles.twoCol}>
                  <div className={dashStyles.section}>
                    <div className={dashStyles.sectionHeader}>
                      <h2 className={dashStyles.sectionTitle}><Layers size={16} /> Costo por módulo</h2>
                      <span className={dashStyles.sectionBadge}>{resumen.filasModulo.length}</span>
                    </div>
                    <div className={dashStyles.sectionBody}>
                      {resumen.filasModulo.length === 0 ? (
                        <p className={dashStyles.emptyState}>Sin datos en este rango.</p>
                      ) : (
                        <div className={dashStyles.chartWrap}>
                          <ResponsiveContainer width="100%" height={Math.max(220, resumen.filasModulo.length * 40)}>
                            <BarChart data={resumen.filasModulo.map((f) => ({ name: moduloLabel(f.modulo), Costo: Number(f.costoEstimado.toFixed(4)) }))} layout="vertical" margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
                              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--color-border-soft)" />
                              <XAxis type="number" tickFormatter={(v: number) => fmtCosto(v)} tick={{ fontSize: 11 }} />
                              <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 11 }} />
                              <Tooltip content={<ChartTooltip />} />
                              <Bar dataKey="Costo" fill="#6366f1" radius={[0, 4, 4, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className={dashStyles.section}>
                    <div className={dashStyles.sectionHeader}>
                      <h2 className={dashStyles.sectionTitle}><Cpu size={16} /> Costo por modelo</h2>
                      <span className={dashStyles.sectionBadge}>{resumen.filasModelo.length}</span>
                    </div>
                    <div className={dashStyles.sectionBody}>
                      {resumen.filasModelo.length === 0 ? (
                        <p className={dashStyles.emptyState}>Sin datos en este rango.</p>
                      ) : (
                        <div className={dashStyles.chartWrap}>
                          <ResponsiveContainer width="100%" height={280}>
                            <PieChart>
                              <Pie
                                data={resumen.filasModelo.map((f) => ({ name: nombreModelo(f.modelo), value: Number(f.costoEstimado.toFixed(4)) }))}
                                dataKey="value"
                                nameKey="name"
                                innerRadius={55}
                                outerRadius={95}
                                paddingAngle={2}
                              >
                                {resumen.filasModelo.map((_, i) => (
                                  <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                                ))}
                              </Pie>
                              <Tooltip content={<ChartTooltip />} />
                              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* ─── Tabla detalle por módulo/modelo ─────── */}
                <div className={dashStyles.twoCol}>
                  <div className={dashStyles.section}>
                    <div className={dashStyles.sectionHeader}>
                      <h2 className={dashStyles.sectionTitle}>Detalle por módulo</h2>
                    </div>
                    <div className={dashStyles.sectionBody}>
                      <div className={dashStyles.tableWrap}>
                        <table className={dashStyles.table}>
                          <thead>
                            <tr>
                              <th>Módulo</th>
                              <th style={{ textAlign: 'right' }}>Tokens</th>
                              <th style={{ textAlign: 'right' }}>Min. audio</th>
                              <th style={{ textAlign: 'right' }}>Caracteres</th>
                              <th style={{ textAlign: 'right' }}>Llamadas</th>
                              <th style={{ textAlign: 'right' }}>Costo</th>
                            </tr>
                          </thead>
                          <tbody>
                            {resumen.filasModulo.map((f) => (
                              <tr key={f.modulo}>
                                <td style={{ fontWeight: 600 }}>{moduloLabel(f.modulo)}</td>
                                <td style={{ textAlign: 'right' }}>{f.tokensTotal > 0 ? fmtTokens(f.tokensTotal) : '—'}</td>
                                <td style={{ textAlign: 'right' }}>{f.audioSegundos > 0 ? fmtMinutos(f.audioSegundos) : '—'}</td>
                                <td style={{ textAlign: 'right' }}>{f.caracteres > 0 ? fmtCaracteres(f.caracteres) : '—'}</td>
                                <td style={{ textAlign: 'right' }}>{f.llamadas}</td>
                                <td style={{ textAlign: 'right', color: 'var(--color-success)', fontWeight: 600 }}>{fmtCosto(f.costoEstimado)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  <div className={dashStyles.section}>
                    <div className={dashStyles.sectionHeader}>
                      <h2 className={dashStyles.sectionTitle}>Detalle por modelo</h2>
                    </div>
                    <div className={dashStyles.sectionBody}>
                      <div className={dashStyles.tableWrap}>
                        <table className={dashStyles.table}>
                          <thead>
                            <tr>
                              <th>Modelo</th>
                              <th>Tipo</th>
                              <th style={{ textAlign: 'right' }}>Cantidad</th>
                              <th style={{ textAlign: 'right' }}>Llamadas</th>
                              <th style={{ textAlign: 'right' }}>Costo</th>
                            </tr>
                          </thead>
                          <tbody>
                            {resumen.filasModelo.map((f) => (
                              <tr key={f.modelo}>
                                <td style={{ fontWeight: 600 }}>{nombreModelo(f.modelo)}</td>
                                <td>{f.tipo === 'stt' ? 'STT' : f.tipo === 'tts' ? 'TTS' : 'LLM'}</td>
                                <td style={{ textAlign: 'right' }}>{fmtCantidad(f.tipo, f)}</td>
                                <td style={{ textAlign: 'right' }}>{f.llamadas}</td>
                                <td style={{ textAlign: 'right', color: 'var(--color-success)', fontWeight: 600 }}>{fmtCosto(f.costoEstimado)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ─── Costo por consulta médica ───────────── */}
                <div className={dashStyles.section}>
                  <div className={dashStyles.sectionHeader}>
                    <h2 className={dashStyles.sectionTitle}>Costo por consulta médica</h2>
                    <span className={dashStyles.sectionBadge}>
                      Top {porConsulta.top.length} de {porConsulta.totalConsultas}
                    </span>
                  </div>
                  <div className={dashStyles.sectionBody}>
                    {loadingRaw ? (
                      <p className={dashStyles.emptyState}>Cargando...</p>
                    ) : porConsulta.top.length === 0 ? (
                      <p className={dashStyles.emptyState}>No hay llamadas de IA asociadas a una consulta en este rango.</p>
                    ) : (
                      <>
                        <div className={dashStyles.tableWrap}>
                          <table className={dashStyles.table}>
                            <thead>
                              <tr>
                                <th>Consulta</th>
                                <th>Fecha</th>
                                <th>Módulos</th>
                                <th style={{ textAlign: 'right' }}>Tokens</th>
                                <th style={{ textAlign: 'right' }}>Min. audio</th>
                                <th style={{ textAlign: 'right' }}>Caracteres</th>
                                <th style={{ textAlign: 'right' }}>Llamadas</th>
                                <th style={{ textAlign: 'right' }}>Costo</th>
                              </tr>
                            </thead>
                            <tbody>
                              {porConsulta.top.map((c) => (
                                <tr
                                  key={c.consultaId}
                                  className={styles.clickableRow}
                                  onClick={() => navigate(`/historial/${c.consultaId}`)}
                                  title="Ver consulta"
                                >
                                  <td style={{ fontFamily: 'monospace', fontSize: 'var(--font-size-xs)' }}>{c.consultaId.slice(0, 10)}…</td>
                                  <td>{c.fecha}</td>
                                  <td>{[...c.modulos].map((m) => moduloLabel(m)).join(', ')}</td>
                                  <td style={{ textAlign: 'right' }}>{c.tokensTotal > 0 ? fmtTokens(c.tokensTotal) : '—'}</td>
                                  <td style={{ textAlign: 'right' }}>{c.audioSegundos > 0 ? fmtMinutos(c.audioSegundos) : '—'}</td>
                                  <td style={{ textAlign: 'right' }}>{c.caracteres > 0 ? fmtCaracteres(c.caracteres) : '—'}</td>
                                  <td style={{ textAlign: 'right' }}>{c.llamadas}</td>
                                  <td style={{ textAlign: 'right', color: 'var(--color-success)', fontWeight: 600 }}>{fmtCosto(c.costoEstimado)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <div className={styles.notice}>
                          <Info size={14} />
                          <span>
                            Calculado a partir de {rawTotalCount != null && rawTotalCount > RAW_LIMIT
                              ? `las ${RAW_LIMIT.toLocaleString()} llamadas más recientes del rango (de ${rawTotalCount.toLocaleString()} en total — para un rango tan amplio no se procesan todas por rendimiento)`
                              : `todas las llamadas del rango (${rawTotalCount ?? 0})`}. Se muestran las 50 consultas de mayor costo.
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* ─── Últimas llamadas (costo de CADA request) ─ */}
                <div className={dashStyles.section}>
                  <div className={dashStyles.sectionHeader}>
                    <h2 className={dashStyles.sectionTitle}><ListOrdered size={16} /> Últimas llamadas</h2>
                    <span className={dashStyles.sectionBadge}>{recientes.length} más recientes</span>
                  </div>
                  <div className={dashStyles.sectionBody}>
                    {loadingRecientes ? (
                      <p className={dashStyles.emptyState}>Cargando...</p>
                    ) : errorRecientes ? (
                      <p className={dashStyles.emptyState} role="alert">No se pudieron leer las últimas llamadas.</p>
                    ) : recientes.length === 0 ? (
                      <p className={dashStyles.emptyState}>Aún no hay llamadas de IA registradas.</p>
                    ) : (
                      <>
                        <div className={dashStyles.tableWrap}>
                          <table className={dashStyles.table}>
                            <thead>
                              <tr>
                                <th scope="col">Cuándo</th>
                                <th scope="col">Módulo</th>
                                <th scope="col">Modelo</th>
                                <th scope="col" className={styles.num}>Entrada</th>
                                <th scope="col" className={styles.num}>Caché</th>
                                <th scope="col" className={styles.num}>Salida</th>
                                <th scope="col" className={styles.num}>Razonamiento</th>
                                <th scope="col" className={styles.num}>Costo entrada / caché / salida</th>
                                <th scope="col" className={styles.num}>Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {recientes.map((r, i) => {
                                const tipo = tipoDe(r.tipo);
                                const cuando = aFecha(r.creadoEn);
                                const desglose = (r.costoEntrada ?? 0) + (r.costoCache ?? 0) + (r.costoSalida ?? 0);
                                return (
                                  <tr key={`${i}-${cuando?.getTime() ?? r.fecha}`}>
                                    <td className={styles.num}>
                                      {cuando
                                        ? cuando.toLocaleString('es-PA', { timeZone: PANAMA_TZ, day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' })
                                        : `${r.fecha ?? '—'}${r.hora ? ` ${r.hora}h` : ''}`}
                                    </td>
                                    <td className={styles.moduloCelda}>{moduloLabel(r.modulo)}</td>
                                    <td>{nombreModelo(r.modelo ?? 'desconocido')}</td>
                                    {tipo === 'llm' ? (
                                      <>
                                        <td className={styles.num}>{fmtTokens(r.tokensIn ?? 0)}</td>
                                        <td className={styles.num}>{r.tokensCache ? fmtTokens(r.tokensCache) : '—'}</td>
                                        <td className={styles.num}>{fmtTokens(r.tokensOut ?? 0)}</td>
                                        <td className={styles.num}>{r.tokensThoughts ? fmtTokens(r.tokensThoughts) : '—'}</td>
                                      </>
                                    ) : (
                                      <>
                                        <td className={styles.num}>{fmtCantidad(tipo, r)}</td>
                                        <td className={`${styles.num} ${styles.muted}`}>—</td>
                                        <td className={`${styles.num} ${styles.muted}`}>—</td>
                                        <td className={`${styles.num} ${styles.muted}`}>—</td>
                                      </>
                                    )}
                                    <td className={`${styles.num} ${styles.muted}`}>
                                      {desglose > 0
                                        ? `${fmtCosto(r.costoEntrada ?? 0)} / ${fmtCosto(r.costoCache ?? 0)} / ${fmtCosto(r.costoSalida ?? 0)}`
                                        : '—'}
                                    </td>
                                    <td className={`${styles.num} ${styles.total}`}>{fmtCosto(r.costoEstimado ?? 0)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        <div className={styles.notice}>
                          <Info size={14} />
                          <span>
                            Las 50 llamadas más recientes de cualquier fecha, con el costo de cada una. «Entrada» incluye
                            los tokens en caché (cobrados con descuento); «Razonamiento» se cobra como salida.
                            Las llamadas de voz muestran minutos de audio o caracteres en lugar de tokens.
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* ─── Editor de precios ────────────────────── */}
                <div className={dashStyles.section}>
                  <div className={dashStyles.sectionHeader}>
                    <h2 className={dashStyles.sectionTitle}><Coins size={16} /> Precios de referencia</h2>
                  </div>
                  <div className={dashStyles.sectionBody}>
                    {preciosLoading ? (
                      <p className={dashStyles.emptyState}>Cargando precios...</p>
                    ) : (
                      <>
                        <h3 style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, margin: '0 0 var(--space-2)', display: 'flex', alignItems: 'center', gap: 'var(--space-1_5)' }}>
                          <Cpu size={14} /> LLM (por millón de tokens)
                        </h3>
                        <div className={styles.priceHeaderRow}>
                          <span>Modelo</span>
                          <span>Entrada ($ / 1M tokens)</span>
                          <span>Salida ($ / 1M tokens)</span>
                        </div>
                        {modelosLLMEditor.length === 0 ? (
                          <p className={dashStyles.emptyState}>Sin modelos LLM vistos aún.</p>
                        ) : modelosLLMEditor.map((modelo) => (
                          <div key={modelo} className={styles.priceRow}>
                            <span style={{ fontWeight: 600 }}>{modelo}</span>
                            <input
                              type="number"
                              min="0"
                              step="0.001"
                              value={preciosLLM[modelo]?.in ?? 0}
                              onChange={(e) => actualizarPrecioLLM(modelo, 'in', e.target.value)}
                            />
                            <input
                              type="number"
                              min="0"
                              step="0.001"
                              value={preciosLLM[modelo]?.out ?? 0}
                              onChange={(e) => actualizarPrecioLLM(modelo, 'out', e.target.value)}
                            />
                          </div>
                        ))}

                        <h3 style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, margin: 'var(--space-5) 0 var(--space-2)', display: 'flex', alignItems: 'center', gap: 'var(--space-1_5)' }}>
                          <Mic size={14} /> STT — transcripción (por minuto de audio)
                        </h3>
                        <div className={styles.priceHeaderRow} style={{ gridTemplateColumns: '1.4fr 1fr' }}>
                          <span>Proveedor</span>
                          <span>$ / minuto de audio</span>
                        </div>
                        {modelosSTTEditor.length === 0 ? (
                          <p className={dashStyles.emptyState}>Sin proveedores STT vistos aún.</p>
                        ) : modelosSTTEditor.map((modelo) => (
                          <div key={modelo} className={styles.priceRow} style={{ gridTemplateColumns: '1.4fr 1fr' }}>
                            <span style={{ fontWeight: 600 }}>{nombreModelo(modelo)}</span>
                            <input
                              type="number"
                              min="0"
                              step="0.0001"
                              value={preciosSTT[modelo] ?? 0}
                              onChange={(e) => actualizarPrecioSTT(modelo, e.target.value)}
                            />
                          </div>
                        ))}

                        <h3 style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, margin: 'var(--space-5) 0 var(--space-2)', display: 'flex', alignItems: 'center', gap: 'var(--space-1_5)' }}>
                          <Volume2 size={14} /> TTS — voz de los agentes (por millón de caracteres)
                        </h3>
                        <div className={styles.priceHeaderRow} style={{ gridTemplateColumns: '1.4fr 1fr' }}>
                          <span>Proveedor</span>
                          <span>$ / 1M caracteres</span>
                        </div>
                        {modelosTTSEditor.length === 0 ? (
                          <p className={dashStyles.emptyState}>Sin proveedores TTS vistos aún.</p>
                        ) : modelosTTSEditor.map((modelo) => (
                          <div key={modelo} className={styles.priceRow} style={{ gridTemplateColumns: '1.4fr 1fr' }}>
                            <span style={{ fontWeight: 600 }}>{nombreModelo(modelo)}</span>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={preciosTTS[modelo] ?? 0}
                              onChange={(e) => actualizarPrecioTTS(modelo, e.target.value)}
                            />
                          </div>
                        ))}

                        <button type="button" className={styles.saveBtn} onClick={guardarPrecios} disabled={preciosSaving}>
                          {preciosSaving ? <Loader2 size={14} className={dashStyles.spinning} /> : <Save size={14} />}
                          Guardar precios
                        </button>
                        <div className={styles.notice}>
                          <Info size={14} />
                          <span>
                            Guardar precios aquí solo afecta llamadas NUEVAS — el costo de llamadas ya registradas no se
                            recalcula (cada una guardó el precio vigente en el momento). Fuente: <code>config/aiPricing</code>.
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
          </>
        )}
        </div>
      </div>
    </MainLayout>
  );
}
